import { cn } from '@/lib/utils';
import { isVoiceDetected } from '@/hooks/useMicrophoneLevel';

export interface MicrophoneLevelMeterProps {
  level: number;
  barCount?: number;
  className?: string;
}

export function MicrophoneLevelMeter({
  level,
  barCount = 12,
  className,
}: MicrophoneLevelMeterProps) {
  const voiceDetected = isVoiceDetected(level);
  const levelPercent = Math.round(level * 100);

  return (
    <div
      className={cn('flex w-fit items-center gap-2', className)}
      role="meter"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={levelPercent}
      aria-label="Microphone input level"
    >
      <div
        className={cn(
          'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
          voiceDetected
            ? 'bg-green-100 dark:bg-green-900/40'
            : 'bg-primary-100 dark:bg-primary-900/40',
        )}
      >
        <svg
          className={cn(
            'h-5 w-5',
            voiceDetected
              ? 'text-green-600 dark:text-green-400'
              : 'text-primary-600 dark:text-primary-400',
          )}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
          />
        </svg>
      </div>

      <div className="flex h-8 items-center gap-1">
        {Array.from({ length: barCount }, (_, index) => {
          const threshold = (index + 1) / barCount;
          const active = level >= threshold * 0.85;

          return (
            <div
              key={index}
              className={cn(
                'h-full w-1.5 shrink-0 rounded-full transition-colors duration-75',
                active
                  ? voiceDetected
                    ? 'bg-green-500 dark:bg-green-400'
                    : 'bg-primary-500 dark:bg-primary-400'
                  : 'bg-slate-200 dark:bg-slate-700',
              )}
            />
          );
        })}
      </div>
    </div>
  );
}
